import { useState } from "react";
import { useGame } from "../../../context/GameContext";
import { Chess } from "chess.js";
import { useMultiplayer } from "../../../context/MultiplayerContext";
import {
    getLastNDays,
    getPuzzleByDate,
    getPuzzleStatus,
    getTodayDateString,
    savePuzzleAttempt,
} from "../../../utils/puzzleHistoryService";
import { getDailyPuzzle, fetchRandomPuzzle } from "../../../utils/puzzleService";
import PuzzleDetailsModal from "./PuzzleDetailsModal";
import "./PuzzleCalendar.scss";

interface PuzzleCalendarProps {
    onClose: () => void;
}

/**
 * PuzzleCalendar component
 * Displays a calendar view of the last 30 days with puzzle completion status
 * Users can select any date to play or replay that day's puzzle
 */
export default function PuzzleCalendar({ onClose }: PuzzleCalendarProps) {
    const { setGameState, setRemoteNavigation } = useGame();
    const { setMultiplayerState } = useMultiplayer();

    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [isLoadingPuzzle, setIsLoadingPuzzle] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    const last30Days = getLastNDays(30);
    const today = getTodayDateString();

    // Group dates by week for better layout
    const weeks: string[][] = [];
    for (let i = 0; i < last30Days.length; i += 7) {
        weeks.push(last30Days.slice(i, i + 7));
    }

    /**
     * Handles clicking on a calendar date
     * - Prevents selecting future dates
     * - Fetches puzzle if it doesn't exist for the selected date
     * - Opens modal with puzzle details
     */
    const handleDateClick = async (date: string) => {
        if (date > today) return;

        setSelectedDate(date);

        const existingPuzzle = getPuzzleByDate(date);
        if (!existingPuzzle) {
            setIsLoadingPuzzle(true);
            try {
                const puzzle = date === today ? await getDailyPuzzle() : await fetchRandomPuzzle();

                if (puzzle) {
                    savePuzzleAttempt(
                        date,
                        {
                            title: puzzle.title,
                            fen: puzzle.fen,
                            pgn: puzzle.pgn,
                            moves: puzzle.moves,
                            playerColor: puzzle.playerColor,
                        },
                        false,
                        0,
                        false,
                    );
                    setRefreshKey((prev) => prev + 1);
                }
            } catch (error) {
                console.error("Error fetching puzzle:", error);
                alert("Failed to load puzzle. Please try again.");
            } finally {
                setIsLoadingPuzzle(false);
            }
        }
    };

    /**
     * Starts playing the selected puzzle
     * - Increments attempt count if first time playing
     * - Initializes game state with puzzle position
     * - Sets player color and puzzle solution
     * - Closes calendar and starts game
     */
    const handlePlayPuzzle = () => {
        if (!selectedDate) return;

        const puzzleData = getPuzzleByDate(selectedDate);
        if (!puzzleData) return;

        if (puzzleData.attempts === 0) {
            savePuzzleAttempt(selectedDate, puzzleData.puzzle, false, 0, true);
        }

        const puzzleGame = new Chess(puzzleData.puzzle.fen);

        setMultiplayerState((prev) => ({
            ...prev,
            playerColor: puzzleData.puzzle.playerColor,
        }));

        setGameState((prev) => ({
            ...prev,
            id: null,
            game: puzzleGame,
            isAIGame: false,
            isPuzzle: true,
            puzzleSolution: puzzleData.puzzle.moves,
            puzzleIndex: 0,
            puzzleDate: selectedDate,
            showGame: true,
            lastMove: null,
        }));

        setRemoteNavigation((prev) => ({
            ...prev,
            remoteOverSquare: "e2",
            remoteClickSquare: null,
        }));

        document.body.style.backgroundImage = "url('images/table2-4k.webp')";
        onClose();
    };

    /** Formats date string as "Jan 15" */
    const formatDate = (dateString: string): string => {
        const date = new Date(dateString + "T00:00:00");
        return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    };

    /** Returns day of week as "Mon", "Tue", etc. */
    const getDayOfWeek = (dateString: string): string => {
        const date = new Date(dateString + "T00:00:00");
        return date.toLocaleDateString("en-US", { weekday: "short" });
    };

    /** Returns CSS class for puzzle status (completed, attempted, not-played) */
    const getStatusClass = (date: string): string => {
        const status = getPuzzleStatus(date);
        return `calendar-day--${status}`;
    };

    // Get puzzle data for selected date (refreshKey ensures fresh data after fetch)
    const selectedPuzzle = selectedDate && refreshKey >= 0 ? getPuzzleByDate(selectedDate) : null;

    return (
        <div className="puzzle-calendar-overlay">
            <div className="puzzle-calendar">
                <div className="puzzle-calendar__header">
                    <h1>Puzzle History</h1>
                    <button className="close-button" onClick={onClose}>
                        ✕
                    </button>
                </div>

                <div className="puzzle-calendar__legend">
                    <div className="legend-item">
                        <span className="legend-dot legend-dot--completed"></span>
                        <span>Completed</span>
                    </div>
                    <div className="legend-item">
                        <span className="legend-dot legend-dot--attempted"></span>
                        <span>In Progress</span>
                    </div>
                    <div className="legend-item">
                        <span className="legend-dot legend-dot--not-played"></span>
                        <span>Not Started</span>
                    </div>
                </div>

                <div className="puzzle-calendar__grid">
                    {weeks.map((week, weekIndex) => (
                        <div key={weekIndex} className="calendar-week">
                            {week.map((date) => {
                                const isToday = date === today;
                                const isSelected = date === selectedDate;
                                const isFuture = date > today;
                                const statusClass = getStatusClass(date);

                                return (
                                    <button
                                        key={date}
                                        className={`calendar-day ${statusClass} ${isToday ? "calendar-day--today" : ""} ${isSelected ? "calendar-day--selected" : ""} ${isFuture ? "calendar-day--future" : ""}`}
                                        onClick={() => handleDateClick(date)}
                                        disabled={isLoadingPuzzle || isFuture}
                                    >
                                        <div className="calendar-day__weekday">
                                            {getDayOfWeek(date)}
                                        </div>
                                        <div className="calendar-day__date">{formatDate(date)}</div>
                                        {!isFuture && (
                                            <>
                                                {statusClass === "calendar-day--completed" && (
                                                    <div className="calendar-day__completed-dot"></div>
                                                )}
                                                {statusClass === "calendar-day--attempted" && (
                                                    <div className="calendar-day__attempted-dot"></div>
                                                )}
                                            </>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>

            {selectedDate && (
                <PuzzleDetailsModal
                    formattedDate={formatDate(selectedDate)}
                    isToday={selectedDate === today}
                    puzzleData={selectedPuzzle}
                    isLoading={isLoadingPuzzle}
                    onPlay={handlePlayPuzzle}
                    onClose={() => setSelectedDate(null)}
                />
            )}
        </div>
    );
}
